import { useEffect, useState } from 'react';
import { api, formatTarih, formatTl, tokens, waLink } from '../lib/api';
import AraSec from '../ui/AraSec';

type Detay = {
  id: number; no: string; title: string; description: string | null;
  kalemler?: { productId: number; code: string; name: string; unit: string; qty: number; not?: string | null;
    stoktanDustu?: boolean; teslimAlinan?: number; fire?: number }[];

  status: string; statusLabel: string; costKurus: number | null;
  contractor: { id: number; name: string; phone: string | null };
  createdAt: string; createdBy: string | null; tokenExpiresAt: string | null;
  portalUrl: string;
  sonrakiDurumlar: string[];
  events: { id: number; status: string; statusLabel: string; note: string | null; byLink: boolean; createdAt: string }[];
  files: { id: number; filename: string; mime: string; size: number }[];
};

const ETIKET: Record<string, string> = {
  KABUL: 'Kabul etti', BASLADI: 'Başladı', BITTI: 'Bitirdi',
  SEVK: 'Sevk edildi', IPTAL: 'İptal',
};

const boyut = (b: number) => (b < 1024 * 1024 ? `${Math.round(b / 1024)} KB` : `${(b / 1048576).toFixed(1)} MB`);

const BIRIM_AD: Record<string, string> = { KUTU: 'kutu', M2: 'm²', ADET: 'adet', KG: 'kg', LT: 'lt', MTUL: 'metretül' };

export default function FasonDetay({
  id, kapat, degisti, yazabilir, maliyetGorur,
}: {
  id: number; kapat: () => void; degisti: () => void;
  yazabilir: boolean; maliyetGorur: boolean;
}) {
  const [d, setD] = useState<Detay | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  /* Fasondan teslim alma: hangi kalem, hangi ürün olarak, kaç adet. */
  const [teslim, setTeslim] = useState<{ sira: number; productId: string; qty: string; kapat: boolean } | null>(null);
  const [urunler, setUrunler] = useState<{ id: number; code: string; name: string }[]>([]);
  useEffect(() => {
    api<{ satirlar: { id: number; code: string; name: string }[] }>('/api/urunler?durum=hepsi&limit=2000&sirala=kod')
      .then((r) => setUrunler(r.satirlar)).catch(() => undefined);
  }, []);
  async function teslimAl() {
    if (!teslim) return;
    setHata(null);
    try {
      await api(`/api/fason/${id}/teslim-al`, { method: 'POST', body: JSON.stringify({
        sira: teslim.sira, productId: Number(teslim.productId), qty: Number(teslim.qty), kalaniKapat: teslim.kapat }) });
      setTeslim(null); await yukle(); degisti();
    } catch (e) { setHata(e instanceof Error ? e.message : 'Teslim alınamadı'); }
  }
  const [bilgi, setBilgi] = useState<string | null>(null);
  const [not, setNot] = useState('');
  const [yukleniyor, setYukleniyor] = useState(false);

  const yukle = () => api<Detay>(`/api/fason/${id}`).then(setD).catch((e) => setHata((e as Error).message));
  useEffect(() => { void yukle(); }, [id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && kapat();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [kapat]);

  async function durumDegistir(status: string) {
    setHata(null);
    try {
      await api(`/api/fason/${id}/durum`, {
        method: 'POST', body: JSON.stringify({ status, note: not || undefined }),
      });
      setNot('');
      setBilgi(`Durum: ${ETIKET[status] ?? status}`);
      await yukle();
      degisti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Durum değiştirilemedi');
    }
  }

  async function dosyaYukle(f: File) {
    setHata(null); setYukleniyor(true);
    try {
      const fd = new FormData();
      fd.append('dosya', f);
      const res = await fetch(`/api/fason/${id}/dosya`, {
        method: 'POST',
        headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {},
        body: fd,
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.error ?? 'Yüklenemedi');
      setBilgi(`${f.name} eklendi`);
      await yukle();
      degisti();
    } catch (e) {
      setHata(e instanceof Error ? e.message : 'Yüklenemedi');
    } finally {
      setYukleniyor(false);
    }
  }

  async function indir(fileId: number, ad: string) {
    const res = await fetch(`/api/fason/dosya/${fileId}`, {
      headers: tokens.access ? { authorization: `Bearer ${tokens.access}` } : {},
    });
    if (!res.ok) return setHata('Dosya indirilemedi');
    const url = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = url; a.download = ad; a.click();
    URL.revokeObjectURL(url);
  }

  /** WhatsApp derin linki — şablon hazır gelsin, Mehmetcan yazmasın. */
  const whatsapp = d && waLink(d.contractor.phone,
        `Merhaba, ${d.no} numaralı iş emri:\n${d.title}\n\nDetay ve dosyalar:\n${d.portalUrl}\n\nArt Mozaik & Seramik`);

  return (
    <div className="ccd-drawer-arka" onClick={kapat} role="presentation">
      <aside className="ccd-drawer" onClick={(e) => e.stopPropagation()}
             role="dialog" aria-modal="true" aria-label={d ? `${d.no} iş emri` : 'İş emri'}>
        {!d ? (
          <div className="ccd-inline-note">Yükleniyor…</div>
        ) : (
          <div className="ccd-stack">
            <div className="ccd-row-between">
              <div>
                <div className="ccd-eyebrow ccd-mono">{d.no}</div>
                <h2 className="ccd-heading">{d.title}</h2>
                <div className="ccd-meta">
                  {d.contractor.name}
                  {d.contractor.phone ? ` · ${d.contractor.phone}` : ''} ·{' '}
                  {formatTarih(d.createdAt)}{d.createdBy ? ` · ${d.createdBy}` : ''}
                </div>
              </div>
              <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={kapat}>Kapat</button>
            </div>

            <div className="ccd-row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <span className="ccd-badge ccd-badge-info">{d.statusLabel}</span>
              {maliyetGorur && d.costKurus != null && (
                <span className="ccd-meta">Maliyet: {formatTl(d.costKurus)}</span>
              )}
            </div>

            {d.kalemler && d.kalemler.length > 0 && (
              <div className="ccd-card ccd-stack-sm">
                <div className="ccd-fieldset-title" style={{ margin: 0 }}>Fasona verilen ürünler</div>
                {d.kalemler.map((k, n) => {
                  const alinan = k.teslimAlinan ?? 0;
                  const kalan = Math.round((k.qty - alinan) * 1000) / 1000;
                  const birim = BIRIM_AD[k.unit] ?? k.unit;
                  return (
                    <div key={n} className="ccd-stack-sm" style={{ borderTop: n ? '1px solid var(--ccd-border)' : undefined, paddingTop: n ? 8 : 0 }}>
                      <div className="ccd-row-between" style={{ flexWrap: 'wrap', gap: 8 }}>
                        <span><b className="ccd-mono">{k.code}</b> {k.name}</span>
                        <span>
                          <b>{k.qty} {birim}</b> verildi
                          {alinan > 0 && <> · <b>{k.fire ? Math.round((alinan - k.fire) * 1000) / 1000 : alinan}</b> alındı</>}
                          {k.fire ? <> · <span className="ccd-kpi-down">{k.fire} fire</span></> : null}
                          {kalan > 0 ? <> · <b className="ccd-kpi-down">{kalan} fasonda</b></> : <span className="ccd-badge ccd-badge-success" style={{ marginLeft: 6 }}>tamam</span>}
                        </span>
                      </div>
                      {!k.stoktanDustu && <div className="ccd-meta">Bu kalem verilirken stoktan düşülmedi.</div>}
                      {yazabilir && kalan > 0 && (teslim?.sira === n ? (
                        <div className="ccd-stack-sm">
                          <div className="ccd-meta">Stoğa hangi ürün olarak, kaç {birim} girecek? İşlenince başka ürüne dönüştüyse onu seçin.</div>
                          <AraSec deger={teslim.productId} degis={(v) => setTeslim({ ...teslim, productId: v })} yerTutucu="Ürün ara: kod ya da ad…"
                                  secenekler={urunler.map((u) => ({ id: u.id, ad: `${u.code} — ${u.name}` }))} />
                          <div className="ccd-row" style={{ gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                            <input className="ccd-input" type="number" inputMode="decimal" step="any" min={0} style={{ width: 100 }} aria-label="Teslim alınan miktar"
                                   value={teslim.qty} onChange={(e) => setTeslim({ ...teslim, qty: e.target.value })} />
                            <label className="ccd-meta" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <input type="checkbox" checked={teslim.kapat} onChange={(e) => setTeslim({ ...teslim, kapat: e.target.checked })} />
                              Eksik kalanı fire say, kalemi kapat
                            </label>
                          </div>
                          <div className="ccd-row" style={{ gap: 8 }}>
                            <button className="ccd-btn ccd-btn-primary ccd-btn-sm" onClick={() => void teslimAl()}>Stoğa gir</button>
                            <button className="ccd-btn ccd-btn-ghost ccd-btn-sm" onClick={() => setTeslim(null)}>Vazgeç</button>
                          </div>
                        </div>
                      ) : (
                        <button className="ccd-btn ccd-btn-sm" style={{ alignSelf: 'flex-start' }}
                                onClick={() => setTeslim({ sira: n, productId: String(k.productId), qty: String(kalan), kapat: false })}>
                          Teslim al → stoğa gir
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
            {d.description && <div className="ccd-card ccd-meta">{d.description}</div>}

            {bilgi && (
              <div className="ccd-banner">
                <span className="ccd-banner-icon" aria-hidden="true">✓</span><span>{bilgi}</span>
              </div>
            )}
            {hata && (
              <div className="ccd-banner ccd-banner-danger">
                <span className="ccd-banner-icon" aria-hidden="true">!</span><span>{hata}</span>
              </div>
            )}

            {/* Paylaşılacak link */}
            <div className="ccd-card ccd-stack">
              <div className="ccd-fieldset-title">Fasoncu bağlantısı</div>
              <p className="ccd-meta" style={{ margin: 0 }}>
                Şifresiz açılır, yalnız bu işi gösterir. Fasoncu buradan dosyaları indirir ve
                "Kabul ettim / Başladım / Bitirdim" der.
                {d.tokenExpiresAt && ` Geçerlilik: ${formatTarih(d.tokenExpiresAt)}.`}
              </p>
              <input className="ccd-input ccd-mono" readOnly value={d.portalUrl}
                     onFocus={(e) => e.currentTarget.select()} aria-label="Fasoncu bağlantısı" />
              <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
                <button className="ccd-btn ccd-btn-sm"
                        onClick={() => { void navigator.clipboard.writeText(d.portalUrl); setBilgi('Bağlantı kopyalandı'); }}>
                  Bağlantıyı kopyala
                </button>
                {whatsapp && (
                  <a className="ccd-btn ccd-btn-accent ccd-btn-sm" href={whatsapp}
                     target="_blank" rel="noreferrer">
                    WhatsApp ile gönder
                  </a>
                )}
              </div>
            </div>

            {/* Dosyalar */}
            <div className="ccd-card ccd-stack">
              <div className="ccd-fieldset-title">Dosyalar ({d.files.length})</div>
              {d.files.length === 0 ? (
                <div className="ccd-meta">Henüz dosya yok. DWG, DXF, PDF, PNG, JPG kabul edilir.</div>
              ) : (
                <ul className="ccd-stack-sm" style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {d.files.map((f) => (
                    <li key={f.id} className="ccd-row-between">
                      <span>📎 {f.filename} <span className="ccd-meta">{boyut(f.size)}</span></span>
                      <button className="ccd-btn ccd-btn-ghost ccd-btn-sm"
                              onClick={() => indir(f.id, f.filename)}>İndir</button>
                    </li>
                  ))}
                </ul>
              )}
              {yazabilir && (
                <div className="ccd-field">
                  <input className="ccd-input" type="file" accept=".dwg,.dxf,.pdf,.png,.jpg,.jpeg"
                         disabled={yukleniyor}
                         onChange={(e) => { const f = e.target.files?.[0]; if (f) void dosyaYukle(f); e.target.value = ''; }} />
                  <div className="ccd-field-hint">{yukleniyor ? 'Yükleniyor…' : 'En fazla 25 MB.'}</div>
                </div>
              )}
            </div>

            {/* Elle ilerletme */}
            {yazabilir && d.sonrakiDurumlar.length > 0 && (
              <div className="ccd-card ccd-stack">
                <div className="ccd-fieldset-title">Panelden ilerlet</div>
                <p className="ccd-meta" style={{ margin: 0 }}>
                  Fasoncu linkten tıklamadıysa buradan ilerletebilirsin.
                </p>
                <div className="ccd-field">
                  <input className="ccd-input" placeholder="Not (isteğe bağlı)"
                         value={not} onChange={(e) => setNot(e.target.value)} />
                </div>
                <div className="ccd-row" style={{ flexWrap: 'wrap' }}>
                  {d.sonrakiDurumlar.map((s) => (
                    <button key={s}
                            className={`ccd-btn ccd-btn-sm${s === 'IPTAL' ? ' ccd-btn-danger' : ' ccd-btn-primary'}`}
                            onClick={() => durumDegistir(s)}>
                      {ETIKET[s] ?? s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Geçmiş */}
            <div className="ccd-card">
              <div className="ccd-fieldset-title">Geçmiş</div>
              <div className="ccd-timeline">
                {d.events.map((e) => (
                  <div className="ccd-tl-item" key={e.id}>
                    <span className="ccd-tl-dot" />
                    <div className="ccd-tl-body">
                      <div className="ccd-tl-head">
                        <span className="ccd-tl-text">{e.statusLabel}</span>
                        <span className="ccd-tl-time">{formatTarih(e.createdAt)}</span>
                      </div>
                      {e.note && <div className="ccd-meta">{e.note}</div>}
                      <div className="ccd-meta ccd-tl-actor">
                        {e.byLink ? 'fasoncu · linkten' : 'panelden'}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </aside>
    </div>
  );
}
